import { BackendApiClient } from './backend-client';
import type { ScopeContext } from '../model/types';
import { BackendValidationError, backendValidationErrorFrom, isBackendRefusal } from '@/shared/lib/backend-errors';
import { UNREADABLE_RESPONSE_CODE, requireObject } from '@/shared/lib/response-shape';
import { userSettingFrom, type UserSetting, type UserSettingPatch } from '@/shared/lib/user-setting';
import { requireAccessList } from './user-access';
import type { UserAccess } from './user-access';

const backendClient = new BackendApiClient();

export type UserStatus = 'created' | 'active' | 'blocked' | 'frozen' | 'closed';

export type UserType = 'person' | 'integration' | 'executive' | 'sysadmin' | 'service' | 'session' | 'mailbox' | 'open';

export interface User {
  id: string;
  name: string;
  reference_id: string;
  type: UserType;
  status: UserStatus;
  source_type?: 'account' | 'holder';
  source_id?: string;
  tax_id?: string;
  allowed_ips?: string[];
  created?: string;
}

export interface UserProfile {
  user: User;
  setting: UserSetting | null;
}

export async function getUserProfile(sessionId: string): Promise<UserProfile> {
  const response = await backendClient.get('/user/profile', sessionId);

  if (!response.success) {
    throw response.validationError
      ?? backendValidationErrorFrom({}, response.error || 'Failed to fetch your profile');
  }

  const user = requireObject<User>(response.data, 'user', 'The profile response carries no readable user');
  if (typeof user.id !== 'string') {
    throw new BackendValidationError('The profile response carries a user with no id', UNREADABLE_RESPONSE_CODE);
  }
  return {
    user,
    setting: userSettingFrom((response.data as { setting?: unknown }).setting),
  };
}

export async function patchUserSetting(
  sessionId: string,
  setting: UserSettingPatch,
): Promise<UserSetting> {
  const response = await backendClient.patch('/user/setting', sessionId, { setting });

  if (!response.success) {
    if (isBackendRefusal(response.validationError)) throw response.validationError;
    throw new BackendValidationError(
      response.error || 'The setting save got no readable answer',
      UNREADABLE_RESPONSE_CODE,
    );
  }

  const saved = userSettingFrom((response.data as { setting?: unknown } | null | undefined)?.setting);
  if (!saved) {
    throw new BackendValidationError('The setting response carries no readable setting', UNREADABLE_RESPONSE_CODE);
  }
  return saved;
}

export async function getOwnAccesses(
  sessionId: string,
  scopeContext: ScopeContext,
): Promise<{ accesses: UserAccess[]; cursor: string }> {
  const response = await backendClient.get('/user/access', sessionId, scopeContext);

  if (!response.success || !response.data) {
    throw response.validationError
      ?? backendValidationErrorFrom({}, response.error || 'Failed to fetch your accesses');
  }

  const accesses = requireAccessList(response.data);
  const cursor = (response.data as { cursor?: unknown }).cursor;
  return { accesses, cursor: typeof cursor === 'string' ? cursor : '' };
}
